/**
 * Enrollment Verification — Bypass (no login required)
 *
 * Accessed via /ev?bt=<bypass_token>
 *
 * The bypass token is a short-lived JWT generated by the backend's
 * GET /api/student/ev-link endpoint (called server-side by ModoLabs).
 *
 * This component makes an unauthenticated call to GET /api/student/ev-bypass?bt=<token>
 * which validates the token and streams back the PDF.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export function EnrollmentVerificationBypass() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('bt');
    if (!token) {
      setErrorMessage('No verification token provided. Please return to the app and try again.');
      setStatus('error');
      return;
    }

    void fetchPdf(token);
  }, []); // intentionally run once on mount

  const fetchPdf = async (token: string) => {
    try {
      const url = `${API_BASE}/student/ev-bypass?bt=${encodeURIComponent(token)}`;
      const response = await fetch(url);

      if (!response.ok) {
        let message = 'An error occurred while retrieving your document.';
        try {
          const body = await response.json();
          if (body?.message) message = body.message;
        } catch {
          // ignore parse failure
        }
        setErrorMessage(message);
        setStatus('error');
        return;
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Try to open in a new tab; fall back to same-window navigation in WebView
      const popup = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      if (!popup) {
        window.location.href = pdfUrl;
      }
      setStatus('success');
    } catch {
      setErrorMessage(
        'A network error occurred. Please check your connection and try again.'
      );
      setStatus('error');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '2rem',
        background: '#f8f9fa',
      }}
    >
      {status === 'loading' && (
        <>
          <div
            style={{
              width: 48,
              height: 48,
              border: '4px solid #dee2e6',
              borderTopColor: '#0d6efd',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              marginBottom: '1.25rem',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#495057', fontSize: '1rem', margin: 0 }}>
            Retrieving your enrollment verification…
          </p>
        </>
      )}

      {status === 'success' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <p style={{ color: '#198754', fontWeight: 600, fontSize: '1.05rem', margin: 0 }}>
            Your enrollment verification document is open.
          </p>
          <p style={{ color: '#6c757d', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            If it did not open, check your browser's popup settings.
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <p
            style={{
              color: '#dc3545',
              fontWeight: 600,
              fontSize: '1.05rem',
              margin: 0,
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </p>
          <p style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '0.75rem' }}>
            If this link has expired, return to the Laurus app and tap the button again.
          </p>
        </>
      )}
    </div>
  );
}
